import { Vector3, Matrix4 } from "three";
import { FeatureStyle, GEOMETRYTYPE } from "../featuretree/internal";
import { STREAMTYPE } from "../measure/internal";
import { USERINPUTTYPE } from "../userinput/userinput";
import { Tool } from "./internal";
/**
 * The Grid Tool
 * @class GridTool
 * @extends {Tool}
 */
export class GridTool extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        // event variables
        this.geoframeeventnr = null;
        this.geoframecollectioneventnr = null;
        this.featurecollection = null;
        this.linestringfeature = null;
        this.spacing = 1.0;
        this.sizeX = 16;
        this.sizeY = 32;
        this.nGridX = 17;
        this.nGridY = 33;
        this.height = 2.5;
        this.shiftX = 0.0;
        this.shiftY = 0.0;
        this._handleKeyEvent = (evt) => {
            let update = false;
            if (evt.keyCode == 107 && !evt.shiftKey && !evt.altKey) {
                //+
                if (this.spacing < this.sizeX && this.spacing < this.sizeY) {
                    this.spacing *= 2.0;
                    update = true;
                }
            }
            if (evt.keyCode == 109 && !evt.shiftKey && !evt.altKey) {
                //-
                if (this.spacing > 0.25) {
                    this.spacing *= 0.5;
                    update = true;
                }
            }
            if ((evt.keyCode == 107 && evt.shiftKey && !evt.altKey) || evt.keyCode == 102) {
                //+ & ctrl
                this.shiftX += 0.05;
                update = true;
            }
            if ((evt.keyCode == 109 && evt.shiftKey && !evt.altKey) || evt.keyCode == 100) {
                //- & ctrl
                this.shiftX -= 0.05;
                update = true;
            }
            if ((evt.keyCode == 107 && !evt.shiftKey && evt.altKey) || evt.keyCode == 104) {
                //+ & ctrl
                this.shiftY += 0.05;
                update = true;
            }
            if ((evt.keyCode == 109 && !evt.shiftKey && evt.altKey) || evt.keyCode == 98) {
                //- & ctrl
                this.shiftY -= 0.05;
                update = true;
            }
            if (update) {
                this._resetGrid();
                this._initGrid(evt);
                this._drawGrid(this.scene.getCurrentGeoFrameCollection().getCurrentGeoFrame());
            }
        };
        this.name = name;
        this.group = group;
        //options - set the default values here....
        const layerstyle = new FeatureStyle({
            stylename: "GridToolStyle",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: 4,
                        stroke: [1, 1, 1, 0.3],
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 1.4,
                        stroke: [1, 0.8, 0, 0.5],
                    },
                },
            ],
        });
        const highlightstyle = new FeatureStyle({
            stylename: "GridToolStyle",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: 2,
                        stroke: [1, 0, 1, 0.5],
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 0.4,
                        stroke: [1, 0, 0, 0.5],
                    },
                },
            ],
        });
        //options - set the default values here....
        const defaultoptions = {
            layerstyle: layerstyle,
            highlightlayerstyle: highlightstyle,
            enablecbf: null,
            disablecbf: null,
            swapheights: true,
            spacing: 1.0,
            sizeX: 16,
            sizeY: 32,
            shiftX: 0.0,
            shiftY: 0.0,
            lock: false,
            spacingchangedcbf: null,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        super.parseOptions(options);
        this.featurecollection = (this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.layerstyle, this.options.highlightlayerstyle, "mmGridTool_internal"));
        this.spacing = this.options.spacing;
        this.sizeX = Math.pow(2, Math.ceil(Math.log(this.options.sizeX) / Math.log(2)));
        this.sizeY = Math.pow(2, Math.ceil(Math.log(this.options.sizeY) / Math.log(2)));
        this.nGridX = this.sizeX / this.spacing + 1;
        this.nGridY = this.sizeY / this.spacing + 1;
        this.shiftX = this.options.shiftX;
        this.shiftY = this.options.shiftY;
        this.type = "GridTool";
    }
    /**
     * internal function - to activate tool call .enable
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        this.fire(GridTool.enabled, this.options["cbobject"]);
        this._initGrid();
        this._onGeoFrameCollectionChanged(this.scene.getCurrentGeoFrameCollection());
        this._subscribeEvents();
        if (!this.options.lock)
            window.addEventListener("keydown", this._handleKeyEvent);
    }
    /**
     * internal function - to disable tool call .disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        // if tool is not active there is nothing to disable...
        if (!this.enabled)
            return;
        if (!this.options.lock)
            window.removeEventListener("keydown", this._handleKeyEvent);
        this._resetGrid();
        this.fire(GridTool.disabled, this.options["cbobject"]);
        this._unSubscribeEvents();
    }
    _subscribeEvents() {
        this.geoframecollectioneventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.GEOFRAMECOLLECTIONCHANGED, this._onGeoFrameCollectionChanged, this);
        this.geoframeeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.GEOFRAMECHANGED, this._drawGrid, this);
        if (this.options.enablecbf != null) {
            this.on(GridTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.on(GridTool.disabled, this.options.disablecbf);
        }
        if (this.options.spacingchangedcbf != null) {
            this.on(GridTool.spacingchanged, this.options.spacingchangedcbf);
        }
    }
    _unSubscribeEvents() {
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.GEOFRAMECHANGED, this.geoframeeventnr);
        this.geoframeeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.GEOFRAMECOLLECTIONCHANGED, this.geoframecollectioneventnr);
        this.geoframecollectioneventnr = null;
        if (this.options.enablecbf != null) {
            this.off(GridTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.off(GridTool.disabled, this.options.disablecbf);
        }
        if (this.options.spacingchangedcbf != null) {
            this.off(GridTool.spacingchanged, this.options.spacingchangedcbf);
        }
    }
    _initGrid(evt) {
        this.nGridX = this.sizeX / this.spacing + 1;
        this.nGridY = this.sizeY / this.spacing + 1;
        for (let i = 0; i < this.nGridX + this.nGridY; i++) {
            this.linestringfeature = this.featurecollection.createFeature();
            this.linestringfeature.geometry = this.linestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        }
        this.fire(GridTool.spacingchanged, this.options["cbobject"], this.spacing, evt);
    }
    _resetGrid() {
        while (this.featurecollection.features.length > 0) {
            this.featurecollection.destroyFeature(this.featurecollection.features[0]);
        }
        this.scene.infrascene.render();
    }
    _onGeoFrameCollectionChanged(geoframecollection) {
        this.height = -1.0 * geoframecollection.getCurrentGeoFrame().getMeasureInfo().height;
        if (this.options.swapheights) {
            if (geoframecollection.type == STREAMTYPE.MONO)
                this.height *= -1.0;
        }
        this._drawGrid(geoframecollection.getCurrentGeoFrame());
    }
    /**
     * Draws the grid on geoframe changed.
     * @method _drawGrid
     * @param geoframe
     * @private
     */
    _drawGrid(geoframe) {
        const pos = new Vector3(geoframe.easting, geoframe.northing, geoframe.height);
        const Omega = (geoframe.omega * Math.PI) / 180;
        const Phi = (geoframe.phi * Math.PI) / 180;
        const Kappa = (geoframe.kappa * Math.PI) / 180;
        const matRot = new Matrix4();
        matRot.set(Math.cos(Phi) * Math.cos(Kappa), -Math.cos(Phi) * Math.sin(Kappa), Math.sin(Phi), 0, Math.cos(Omega) * Math.sin(Kappa) + Math.sin(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.cos(Omega) * Math.cos(Kappa) - Math.sin(Omega) * Math.sin(Phi) * Math.sin(Kappa), -Math.sin(Omega) * Math.cos(Phi), 0, Math.sin(Omega) * Math.sin(Kappa) - Math.cos(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.sin(Omega) * Math.cos(Kappa) + Math.cos(Omega) * Math.sin(Phi) * Math.sin(Kappa), Math.cos(Omega) * Math.cos(Phi), 0, 0, 0, 0, 1);
        const Omega2 = ((-1 * 90 - geoframe.getMeasureInfo().tilt) * Math.PI) / 180;
        const Phi2 = 0.0;
        const Kappa2 = (geoframe.getMeasureInfo().orientation * Math.PI) / 180;
        const matRot2 = new Matrix4();
        matRot2.set(Math.cos(Phi2) * Math.cos(Kappa2), -Math.cos(Phi2) * Math.sin(Kappa2), Math.sin(Phi2), 0, Math.cos(Omega2) * Math.sin(Kappa2) + Math.sin(Omega2) * Math.sin(Phi2) * Math.cos(Kappa2), Math.cos(Omega2) * Math.cos(Kappa2) - Math.sin(Omega2) * Math.sin(Phi2) * Math.sin(Kappa2), -Math.sin(Omega2) * Math.cos(Phi2), 0, Math.sin(Omega2) * Math.sin(Kappa2) - Math.cos(Omega2) * Math.sin(Phi2) * Math.cos(Kappa2), Math.sin(Omega2) * Math.cos(Kappa2) + Math.cos(Omega2) * Math.sin(Phi2) * Math.sin(Kappa2), Math.cos(Omega2) * Math.cos(Phi2), 0, 0, 0, 0, 1);
        matRot.multiply(matRot2);
        const heightshift = new Vector3(0, 0, this.height);
        for (let i = 0; i < this.nGridX; i++) {
            const curfeaturex = this.featurecollection.features[i];
            const fpx = new Vector3(-1 * this.sizeX * 0.5 + i * this.spacing + this.shiftX, -1 * this.sizeY * 0.5 + this.shiftY, 0.0);
            const lpx = new Vector3(-1 * this.sizeX * 0.5 + i * this.spacing + this.shiftX, this.sizeY * 0.5 + this.shiftY, 0.0);
            const pX1 = fpx.applyMatrix4(matRot).add(pos).add(heightshift).toArray();
            const pX2 = lpx.applyMatrix4(matRot).add(pos).add(heightshift).toArray();
            curfeaturex.geometry.coordinates = [pX1, pX2];
        }
        for (let j = 0; j < this.nGridY; j++) {
            const curfeaturey = this.featurecollection.features[this.nGridX + j];
            const fpy = new Vector3(-1.0 * this.sizeX * 0.5 + this.shiftX, -1 * this.sizeY * 0.5 + j * this.spacing + this.shiftY, 0.0);
            const lpy = new Vector3(this.sizeX * 0.5 + this.shiftX, -1 * this.sizeY * 0.5 + j * this.spacing + this.shiftY, 0.0);
            const pY1 = fpy.applyMatrix4(matRot).add(pos).add(heightshift).toArray();
            const pY2 = lpy.applyMatrix4(matRot).add(pos).add(heightshift).toArray();
            curfeaturey.geometry.coordinates = [pY1, pY2];
        }
        this.featurecollection.update();
    }
}
GridTool.enabled = "enabled";
GridTool.disabled = "disabled";
GridTool.spacingchanged = "spacingchanged";
//# sourceMappingURL=gridtool.js.map